import { Command } from '@/components/shared';

/**
 * Centralized command definitions for the application
 * All commands with their roles, shortcuts, and actions are defined here
 */

export type CommandContext = {
    // Navigation functions
    navigate: (path: string) => void;

    // Admin dashboard actions
    setShowCreateUserModal?: (show: boolean) => void;
    setShowCreateTeamModal?: (show: boolean) => void;
    setShowCreateOrgModal?: (show: boolean) => void;
    setActiveTab?: (tab: string) => void;
    fetchDashboardData?: () => void;

    // Coach dashboard actions
    setActiveView?: (view: string) => void;
    setDays?: (days: number) => void;
    fetchTeamsData?: () => void;

    // Personal dashboard actions
    fetchHRVData?: () => void;
    fetchSleepData?: () => void;
    fetchActivityData?: () => void;
    refreshUserInfo?: () => void;
    triggerDataSync?: () => void;
    setShowDebugPanel?: () => void;

    // Team Information actions
    setTeamInfoTab?: (tab: string) => void;
    fetchTeamData?: () => void;

    // Athlete Detail actions
    setAthleteDetailTab?: (tab: string) => void;
};

/**
 * Generate all available commands based on the provided context
 * @param context - Context object containing navigation and action functions
 * @returns Array of Command objects - ALL commands for all roles, not just context-specific ones
 */
export const generateCommands = (context: CommandContext): Command[] => {
    const commands: Command[] = [];

    // ========== ADMIN COMMANDS ==========
    // These commands are now available to both admins and coaches
    // Backend handles permission differences (coaches cannot delete things they didn't create)
    commands.push(
        {
            id: 'create-user',
            label: 'Create User',
            icon: '👤',
            description: 'Add a new user to the system',
            keywords: ['add', 'new', 'user', 'account', 'member', 'create user', 'add user', 'register', 'athlete', 'coach', 'signup'],
            action: () => {
                if (context.setShowCreateUserModal) {
                    context.setShowCreateUserModal(true);
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            shortcut: ['cmd', 'shift', 'u'],
            roles: ['admin', 'coach']
        },
        {
            id: 'create-team',
            label: 'Create Team',
            icon: '🏆',
            description: 'Create a new team',
            keywords: ['add', 'new', 'team', 'group', 'create team', 'add team', 'squad', 'setup team'],
            action: () => {
                if (context.setShowCreateTeamModal) {
                    context.setShowCreateTeamModal(true);
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            shortcut: ['cmd', 'shift', 't'],
            roles: ['admin', 'coach']
        },
        {
            id: 'create-organization',
            label: 'Create Organization',
            icon: '🏢',
            description: 'Add a new organization',
            keywords: ['add', 'new', 'organization', 'org', 'company', 'create organization', 'add org', 'club', 'school', 'setup org'],
            action: () => {
                if (context.setShowCreateOrgModal) {
                    context.setShowCreateOrgModal(true);
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            shortcut: ['cmd', 'shift', 'o'],
            roles: ['admin', 'coach']
        },
        {
            id: 'navigate-overview',
            label: 'Go to Admin Overview',
            icon: '📊',
            description: 'View dashboard overview and analytics',
            keywords: ['overview', 'dashboard', 'stats', 'analytics', 'home', 'admin', 'summary', 'main', 'admin dashboard'],
            action: () => {
                if (context.setActiveTab) {
                    context.setActiveTab('overview');
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin', 'coach']
        },
        {
            id: 'navigate-users',
            label: 'Go to Users Management',
            icon: '👥',
            description: 'Manage users and permissions',
            keywords: ['users', 'members', 'accounts', 'admin', 'people', 'user management', 'permissions', 'roles', 'athletes', 'coaches'],
            action: () => {
                if (context.setActiveTab) {
                    context.setActiveTab('users');
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin', 'coach']
        },
        {
            id: 'navigate-teams',
            label: 'Go to Teams Management',
            icon: '🏆',
            description: 'View and manage teams',
            keywords: ['teams', 'groups', 'admin', 'team management', 'squads', 'roster', 'manage teams'],
            action: () => {
                if (context.setActiveTab) {
                    context.setActiveTab('teams');
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin', 'coach']
        },
        {
            id: 'navigate-organizations',
            label: 'Go to Organizations Management',
            icon: '🏢',
            description: 'Manage organizations',
            keywords: ['organizations', 'orgs', 'companies', 'admin', 'organization management', 'clubs', 'schools', 'institutions'],
            action: () => {
                if (context.setActiveTab) {
                    context.setActiveTab('organizations');
                } else {
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin', 'coach']
        },
        {
            id: 'refresh-admin-data',
            label: 'Refresh Admin Data',
            icon: '🔄',
            description: 'Reload all dashboard data',
            keywords: ['refresh', 'reload', 'update', 'sync', 'admin'],
            action: () => {
                if (context.fetchDashboardData) {
                    context.fetchDashboardData();
                } else {
                    // Navigate to Admin Dashboard if not already there
                    context.navigate('/admin-dashboard');
                }
            },
            roles: ['admin', 'coach']
        }
    );

    // ========== COACH COMMANDS ==========
    commands.push(
        {
            id: 'navigate-team-overview',
            label: 'Go to Team Overview',
            icon: '📊',
            description: 'View team health and performance summary',
            keywords: ['overview', 'dashboard', 'summary', 'team', 'coach', 'team overview', 'coach dashboard', 'health', 'performance', 'insights'],
            action: () => {
                if (context.setActiveView) {
                    context.setActiveView('overview');
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-analytics',
            label: 'Go to Analytics Dashboard',
            icon: '📈',
            description: 'View detailed charts and trends',
            keywords: ['analytics', 'charts', 'graphs', 'trends', 'coach', 'analytics dashboard', 'data', 'visualization', 'metrics', 'stats'],
            action: () => {
                if (context.setActiveView) {
                    context.setActiveView('analytics');
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-rankings',
            label: 'Go to Rankings',
            icon: '🏆',
            description: 'View athlete performance rankings',
            keywords: ['rankings', 'leaderboard', 'standings', 'coach', 'ranking', 'top', 'best', 'compare', 'comparison'],
            action: () => {
                if (context.setActiveView) {
                    context.setActiveView('rankings');
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-athletes',
            label: 'Go to Athletes',
            icon: '🏃‍♂️',
            description: 'View athlete roster and details',
            keywords: ['athletes', 'players', 'team', 'roster', 'coach', 'athlete list', 'members', 'squad', 'team members'],
            action: () => {
                if (context.setActiveView) {
                    context.setActiveView('athletes');
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'navigate-chat',
            label: 'Open AI Chat',
            icon: '🤖',
            description: 'Chat with AI about team insights',
            keywords: ['chat', 'ai', 'assistant', 'help', 'coach', 'ai chat', 'ask', 'question', 'chatbot', 'gpt'],
            action: () => {
                if (context.setActiveView) {
                    context.setActiveView('chat');
                } else {
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'refresh-team-data',
            label: 'Refresh Team Data',
            icon: '🔄',
            description: 'Reload all team metrics',
            keywords: ['refresh', 'reload', 'update', 'sync', 'coach'],
            action: () => {
                if (context.fetchTeamsData) {
                    context.fetchTeamsData();
                } else {
                    // Navigate to Coach Dashboard if not already there
                    context.navigate('/coach-dashboard');
                }
            },
            roles: ['coach', 'admin']
        }
    );

    // ========== PERIOD SELECTION COMMANDS ==========
    // Available on screens with time-based data - navigates to Personal Dashboard if not on a compatible page
    commands.push(
        {
            id: 'change-period-7',
            label: 'View Last 7 Days',
            icon: '📅',
            description: 'Change period to last 7 days',
            keywords: ['period', 'time', 'week', '7 days', '7', 'seven', 'last week', 'timeframe', 'range', 'date'],
            action: () => {
                if (context.setDays) {
                    context.setDays(7);
                } else {
                    // Navigate to Personal Dashboard if not on a page with time controls
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-14',
            label: 'View Last 14 Days',
            icon: '📅',
            description: 'Change period to last 2 weeks',
            keywords: ['period', 'time', '2 weeks', '14 days', '14', 'fourteen', 'two weeks', 'fortnight', 'timeframe'],
            action: () => {
                if (context.setDays) {
                    context.setDays(14);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-30',
            label: 'View Last 30 Days',
            icon: '📅',
            description: 'Change period to last 30 days',
            keywords: ['period', 'time', 'month', '30 days', '30', 'thirty', 'last month', 'monthly', 'timeframe'],
            action: () => {
                if (context.setDays) {
                    context.setDays(30);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-60',
            label: 'View Last 60 Days',
            icon: '📅',
            description: 'Change period to last 60 days',
            keywords: ['period', 'time', '60 days', '2 months', '60', 'sixty', 'two months', 'timeframe'],
            action: () => {
                if (context.setDays) {
                    context.setDays(60);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'change-period-90',
            label: 'View Last 90 Days',
            icon: '📅',
            description: 'Change period to last 90 days',
            keywords: ['period', 'time', '90 days', '3 months', '90', 'ninety', 'three months', 'quarter', 'timeframe'],
            action: () => {
                if (context.setDays) {
                    context.setDays(90);
                } else {
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        }
    );

    // ========== PERSONAL DASHBOARD COMMANDS ==========
    commands.push(
        {
            id: 'refresh-personal-data',
            label: 'Sync My Data (Celery)',
            icon: '🔄',
            description: 'Trigger backend data sync via Celery task',
            keywords: ['refresh', 'reload', 'update', 'sync', 'personal', 'refresh data', 'update data', 'hrv', 'sleep', 'activity', 'celery', 'background', 'task'],
            action: () => {
                if (context.triggerDataSync) {
                    context.triggerDataSync();
                } else {
                    // Navigate to Personal Dashboard if not already there
                    context.navigate('/dashboard');
                }
            },
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'toggle-debug',
            label: 'Toggle Debug Panel',
            icon: '🔧',
            description: 'Show/hide debug information',
            keywords: ['debug', 'developer', 'troubleshoot', 'dev', 'debugging', 'console', 'logs'],
            action: () => {
                if (context.setShowDebugPanel) {
                    context.setShowDebugPanel();
                } else {
                    // Navigate to Personal Dashboard where debug panel is available
                    context.navigate('/dashboard');
                }
            },
            roles: ['admin']
        }
    );

    // ========== TEAM INFORMATION COMMANDS ==========
    commands.push(
        {
            id: 'team-info-overview',
            label: 'Go to Team Info Overview',
            icon: '📊',
            description: 'View team overview and details',
            keywords: ['team', 'overview', 'details', 'info', 'team information', 'team overview', 'team details', 'about team'],
            action: () => {
                if (context.setTeamInfoTab) {
                    context.setTeamInfoTab('overview');
                } else {
                    context.navigate('/team-information');
                }
            },
            roles: ['coach', 'admin', 'athlete']
        },
        {
            id: 'team-info-codes',
            label: 'Go to Team Join Codes',
            icon: '🔑',
            description: 'View team join codes',
            keywords: ['team', 'codes', 'join', 'invite', 'join codes', 'invitation', 'team codes', 'access codes'],
            action: () => {
                if (context.setTeamInfoTab) {
                    context.setTeamInfoTab('codes');
                } else {
                    context.navigate('/team-information');
                }
            },
            roles: ['coach', 'admin']
        },
        {
            id: 'team-info-members',
            label: 'Go to Team Members',
            icon: '👥',
            description: 'View team members and roster',
            keywords: ['team', 'members', 'roster', 'athletes', 'team members', 'team roster', 'players', 'squad'],
            action: () => {
                if (context.setTeamInfoTab) {
                    context.setTeamInfoTab('members');
                } else {
                    context.navigate('/team-information');
                }
            },
            roles: ['coach', 'admin', 'athlete']
        },
        {
            id: 'refresh-team-info',
            label: 'Refresh Team Information',
            icon: '🔄',
            description: 'Reload team data and details',
            keywords: ['refresh', 'reload', 'update', 'team'],
            action: () => {
                if (context.fetchTeamData) {
                    context.fetchTeamData();
                } else {
                    // Navigate to Team Information page if not already there
                    context.navigate('/team-information');
                }
            },
            roles: ['coach', 'admin', 'athlete']
        }
    );

    // ========== ATHLETE DETAIL COMMANDS ==========
    // These are only shown when viewing an athlete (context aware)
    if (context.setAthleteDetailTab) {
        commands.push(
            {
                id: 'athlete-detail-overview',
                label: 'Go to Athlete Overview Tab',
                icon: '📊',
                description: 'View athlete performance overview',
                keywords: ['athlete', 'overview', 'summary', 'performance', 'athlete overview', 'athlete detail', 'stats', 'metrics'],
                action: () => context.setAthleteDetailTab!('overview'),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-recovery',
                label: 'Go to Athlete Recovery Tab',
                icon: '💚',
                description: 'View athlete recovery metrics',
                keywords: ['athlete', 'recovery', 'hrv', 'health', 'athlete recovery', 'recovery score', 'readiness', 'rhr'],
                action: () => context.setAthleteDetailTab!('recovery'),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-sleep',
                label: 'Go to Athlete Sleep Tab',
                icon: '😴',
                description: 'View athlete sleep analysis',
                keywords: ['athlete', 'sleep', 'rest', 'quality', 'athlete sleep', 'sleep data', 'rem', 'deep sleep'],
                action: () => context.setAthleteDetailTab!('sleep'),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-activity',
                label: 'Go to Athlete Activity Tab',
                icon: '🔥',
                description: 'View athlete activity and strain',
                keywords: ['athlete', 'activity', 'strain', 'workout', 'athlete activity', 'training', 'exercise', 'workouts'],
                action: () => context.setAthleteDetailTab!('activity'),
                roles: ['coach', 'admin']
            },
            {
                id: 'athlete-detail-ai-insights',
                label: 'Go to Athlete AI Insights Tab',
                icon: '🤖',
                description: 'View AI-powered performance insights',
                keywords: ['athlete', 'ai', 'insights', 'analysis', 'recommendations', 'athlete insights', 'ai analysis', 'suggestions'],
                action: () => context.setAthleteDetailTab!('ai-insights'),
                roles: ['coach', 'admin']
            }
        );
    }

    // ========== GLOBAL NAVIGATION COMMANDS ==========
    commands.push(
        {
            id: 'nav-dashboard',
            label: 'Go to Dashboard',
            icon: '📊',
            description: 'View your dashboard with personal and team data',
            keywords: ['dashboard', 'home', 'personal', 'team', 'coach', 'athlete', 'my dashboard', 'my data', 'my stats', 'health', 'hrv', 'analytics'],
            action: () => context.navigate('/dashboard'),
            roles: ['athlete', 'coach', 'admin']
        },
        {
            id: 'nav-team-info',
            label: 'Go to Team Information',
            icon: 'ℹ️',
            description: 'View team details and information',
            keywords: ['team', 'info', 'information', 'details'],
            action: () => context.navigate('/team-information'),
            roles: ['coach', 'admin']
        },
        {
            id: 'nav-admin-dashboard',
            label: 'Go to Admin Dashboard',
            icon: '⚙️',
            description: 'Access admin controls and management',
            keywords: ['admin', 'dashboard', 'management', 'settings', 'control panel'],
            action: () => context.navigate('/admin-dashboard'),
            roles: ['admin', 'coach']
        },
        {
            id: 'nav-register',
            label: 'Register New User',
            icon: '➕',
            description: 'Create a new user account',
            keywords: ['register', 'signup', 'new', 'user'],
            action: () => context.navigate('/register'),
            roles: ['admin', 'coach']
        },
        {
            id: 'nav-connect-whoop',
            label: 'Connect Whoop Device',
            icon: '⌚',
            description: 'Link your Whoop device',
            keywords: ['whoop', 'connect', 'device', 'wearable'],
            action: () => context.navigate('/connect-whoop'),
            roles: ['athlete', 'coach', 'admin']
        }
    );

    return commands;
};

